import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge, makeStyles } from "@material-ui/core";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { brand } from "../styles/brand";

const useStyles = makeStyles((theme) => ({
  menuItem: {
    minHeight: 48,
    margin: theme.spacing(0.5, 1.25),
    width: "auto",
    borderRadius: 12,
    color: "rgba(255,255,255,.72)",
    "& .MuiListItemIcon-root": {
      minWidth: 40,
      color: "rgba(255,255,255,.68)",
    },
    "& .MuiListItemText-primary": {
      fontSize: 14,
      fontWeight: 600,
    },
    "&:hover": {
      color: "#FFFFFF",
      backgroundColor: "rgba(255,255,255,.10)",
    },
    "&.Mui-selected": {
      color: "#FFFFFF",
      background: "rgba(255,255,255,.11)",
      boxShadow: `inset 3px 0 0 ${brand.sky}`,
      "& .MuiListItemIcon-root": { color: brand.sky },
      "&:hover": { backgroundColor: "rgba(20,169,229,.24)" },
    },
  },
  subheader: {
    color: "rgba(255,255,255,.42)",
    fontSize: 10,
    fontWeight: 800,
    lineHeight: "34px",
    letterSpacing: ".12em",
    textTransform: "uppercase",
  },
  divider: { backgroundColor: "rgba(255,255,255,.09)", margin: theme.spacing(1.5, 2) },
}));

function ListItemLink(props) {
  const { icon, primary, to, className, selected } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem button component={renderLink} className={className} selected={selected}>
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} />
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const { drawerClose } = props;
  const classes = useStyles();
  const location = useLocation();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose}>
      <ListItemLink
        to="/"
        primary="Dashboard"
        icon={<DashboardOutlinedIcon />}
        selected={location.pathname === "/"}
        className={classes.menuItem}
      />
      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
            <SyncAltIcon />
          </Badge>
        }
        selected={location.pathname.startsWith("/connections")}
        className={classes.menuItem}
      />
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
        selected={location.pathname.startsWith("/tickets")}
        className={classes.menuItem}
      />

      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
        selected={location.pathname.startsWith("/contacts")}
        className={classes.menuItem}
      />
      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
        selected={location.pathname.startsWith("/quickAnswers")}
        className={classes.menuItem}
      />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider className={classes.divider} />
            <ListSubheader inset className={classes.subheader}>
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              selected={location.pathname.startsWith("/users")}
              className={classes.menuItem}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              selected={location.pathname.toLowerCase().startsWith("/queues")}
              className={classes.menuItem}
            />
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              selected={location.pathname.toLowerCase().startsWith("/settings")}
              className={classes.menuItem}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
